import { Sparkles, ChevronRight, Clock, CheckCircle2, AlertCircle, Loader2, XCircle, BookOpen } from 'lucide-react';
import SocialSelector from './SocialSelector';

const STATUS_META = {
  pending: { label: 'En cola', color: 'text-white/50', Icon: Clock },
  running: { label: 'En proceso', color: 'text-neon-red', Icon: Loader2, spin: true },
  completed: { label: 'Completado', color: 'text-emerald-400', Icon: CheckCircle2 },
  failed: { label: 'Error', color: 'text-red-400', Icon: AlertCircle },
  needs_approval: { label: 'Requiere aprobación', color: 'text-amber-400', Icon: AlertCircle },
  cancelled: { label: 'Cancelado', color: 'text-white/30', Icon: XCircle },
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('es-ES', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
    });
  } catch { return iso; }
};

const ProjectCard = ({ project, jobs, onOpenJob, onOpenBible }) => {
  const activeJobs = jobs.filter(j => j.status === 'pending' || j.status === 'running');

  return (
    <div
      className="bg-[#0F0F0F] border border-neon-red/20 hover:border-neon-red/40 transition-colors"
      data-testid={`project-card-${project.project_slug}`}
    >
      {/* Project header */}
      <div className="px-5 py-4 border-b border-neon-red/10">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <Sparkles className="w-4 h-4 text-neon-red mt-1 shrink-0" />
            <div className="min-w-0">
              <h3 className="font-outfit text-lg font-bold text-white">
                {project.name}
              </h3>
              <p className="font-jetbrains text-xs text-white/50 mt-0.5">
                {project.description}
              </p>
              <p className="font-jetbrains text-[10px] text-white/40 mt-2 tracking-widest uppercase">
                {project.language === 'he' ? 'Hebreo' : project.language === 'es' ? 'Español' : project.language}
                {' · '}
                {jobs.length} {jobs.length === 1 ? 'trabajo' : 'trabajos'}
                {activeJobs.length > 0 && (
                  <span className="text-neon-red ml-2">
                    · {activeJobs.length} activo{activeJobs.length !== 1 && 's'}
                  </span>
                )}
              </p>
            </div>
          </div>
          {onOpenBible && (
            <button
              onClick={() => onOpenBible(project.project_slug, project.name)}
              data-testid={`open-bible-${project.project_slug}`}
              className="flex items-center gap-1.5 px-2 py-1.5 bg-black/40 border border-neon-red/30 hover:border-neon-red hover:bg-neon-red/20 text-white/80 hover:text-white transition-all text-[10px] font-jetbrains tracking-widest uppercase whitespace-nowrap"
              title="Gestionar Bible del proyecto"
            >
              <BookOpen className="w-3.5 h-3.5" />
              Bible
            </button>
          )}
        </div>
      </div>

      {/* Per-project social platform selector (UI-only, no publishing) */}
      <SocialSelector projectSlug={project.project_slug} projectName={project.name} />

      {/* Jobs list */}
      <div className="divide-y divide-white/5">
        {jobs.length === 0 && (
          <p className="px-5 py-4 font-jetbrains text-xs text-white/40 italic">
            Sin trabajos aún. Pide a Alivan que produzca algo para este canal.
          </p>
        )}
        {jobs.map(job => {
          const meta = STATUS_META[job.status] || STATUS_META.pending;
          const Icon = meta.Icon;
          return (
            <button
              key={job.job_id}
              onClick={() => onOpenJob(job.job_id)}
              data-testid={`job-item-${job.job_id}`}
              className="w-full flex items-start gap-3 px-5 py-3 text-left hover:bg-white/[0.03] transition-colors"
            >
              <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${meta.color} ${meta.spin ? 'animate-spin' : ''}`} />
              <div className="flex-1 min-w-0">
                <p className="font-jetbrains text-sm text-white truncate">
                  {job.task_description}
                </p>
                <div className="flex items-center gap-3 mt-1">
                  <span className={`font-jetbrains text-[10px] uppercase tracking-widest ${meta.color}`}>
                    {meta.label}
                  </span>
                  <span className="font-jetbrains text-[10px] text-white/40">
                    {formatDate(job.created_at)}
                  </span>
                  {(job.status === 'running' || job.status === 'pending') && (
                    <span className="font-jetbrains text-[10px] text-white/50">
                      {job.progress}%
                    </span>
                  )}
                </div>
                {job.progress_message && job.status !== 'completed' && (
                  <p className="font-jetbrains text-[11px] text-white/40 mt-1 truncate">
                    {job.progress_message}
                  </p>
                )}
              </div>
              <ChevronRight className="w-4 h-4 text-white/30 shrink-0" />
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ProjectCard;
